// ---------------------------------------------------------------------------
// Field catalog — the mappable data fields for each report source.
//
// A report "domain" (Sub Order / Imports / Exports) maps to one or more fine
// "sources". Each source declares a tree of fields; collection fields carry
// element-relative `children` (used for DATA_TABLE columns).
//
// Field paths are written to match the ACTUAL JSON each endpoint returns
// (note the mixed snake_case / camelCase — e.g. product_code vs itemName).
// ---------------------------------------------------------------------------

export const FIELD_TYPES = {
  STRING: "string",
  NUMBER: "number",
  CURRENCY: "currency",
  DATE: "date",
  ENUM: "enum",
  COLLECTION: "collection",
};

export const DOMAINS = [
  { key: "SUB_ORDER", label: "Sub Order" },
  { key: "IMPORTS", label: "Imports" },
  { key: "EXPORTS", label: "Exports" },
];

// Fine-grained sources (drive which endpoint + catalog to use).
export const SOURCES = [
  { key: "SUB_ORDER", label: "Sub Order", domain: "SUB_ORDER" },
  { key: "IMPORT_ITEM", label: "Import Item", domain: "IMPORTS" },
  { key: "IMPORT_BILL", label: "Import Bill", domain: "IMPORTS" },
  { key: "EXPORT_PRODUCT", label: "Export Product", domain: "EXPORTS" },
  { key: "EXPORT_PRODUCTION", label: "Export Production", domain: "EXPORTS" },
];

const f = (label, path, type) => ({ label, path, type });
const coll = (label, path, children) => ({
  label,
  path,
  type: "collection",
  isCollection: true,
  children,
});

export const CATALOG = {
  // -------- SUB_ORDER : GET /api/sub-order/{id} (raw entity) --------
  SUB_ORDER: {
    label: "Sub Order",
    fields: [
      f("Order No", "orderId", "number"),
      // Order-level product (derived server-side from the line items' shared SubOrderProduct).
      f("Product", "productName", "string"),
      f("Status", "status", "enum"),
      f("Payment Status", "paymentStatus", "string"),
      f("Total Cost", "cost", "currency"),
      f("Material Cost", "materialCost", "currency"),
      f("Labour Cost", "labourCost", "currency"),
      f("Transport Cost", "transportCost", "currency"),
      f("Paid Amount", "paidAmount", "currency"),
      f("Order Date", "orderDate", "date"),
      f("Added Date", "addedDate", "date"),
      f("Supplier", "subOrderPerson.personName", "string"),
      f("Supplier Phone", "subOrderPerson.phone", "string"),
      f("Supplier Address", "subOrderPerson.address", "string"),
      f("Supplier Email", "subOrderPerson.email", "string"),
      f("Supplier Credit Balance", "subOrderPerson.creditBalance", "currency"),
      coll("Line Items", "lineItems[]", [
        f("Type", "lineItemType", "enum"),
        f("Product Code", "imports.product_code", "string"),
        f("Product Name", "imports.itemName", "string"),
        f("Brand", "imports.brand", "string"),
        f("Unit Type", "imports.unitType", "string"),
        f("Ordered Qty", "orderedQty", "number"),
        f("Item Unit", "itemUnit", "string"),
        f("Received Qty", "totalReceivedQty", "number"),
        f("Checked Qty", "totalCheckedQty", "number"),
        f("Damaged Qty", "totalDamagedQty", "number"),
        f("Completion %", "receivingCompletionPercentage", "number"),
      ]),
      coll("Payments", "subOrderPayment[]", [
        f("Payment Date", "paymentDate", "date"),
        f("Amount", "amount", "currency"),
        f("Status", "status", "string"),
        f("Note", "note", "string"),
        f("Added By", "addedBy", "string"),
        f("Approved By", "approvedBy", "string"),
      ]),
    ],
    derived: {
      balance: { label: "Balance", op: "subtract", left: "cost", right: "paidAmount" },
    },
  },

  // -------- IMPORT_ITEM : GET /api/import/{id} (raw entity) --------
  IMPORT_ITEM: {
    label: "Import Item",
    fields: [
      f("Import Id", "importId", "number"),
      f("Product Code", "product_code", "string"),
      f("Item Name", "itemName", "string"),
      f("Unit Type", "unitType", "string"),
      f("Qty In Stock", "qty", "number"),
      f("Refill Rate", "refillRate", "number"),
      f("Brand", "brand", "string"),
      f("Note", "note", "string"),
      f("Last Unit Price", "lastUpdatedUnitPrice", "currency"),
      f("Current Price", "currentPriceValue", "currency"),
      f("Status", "status", "string"),
      f("Added By", "addedBy", "string"),
      f("Added Date", "addedDate", "date"),
      coll("Categories", "im_categories[]", [
        f("Category", "category", "string"),
        f("Sub Cat 1", "subCat_1", "string"),
        f("Sub Cat 2", "subCat_2", "string"),
        f("Sub Cat 3", "subCat_3", "string"),
      ]),
      coll("Price History", "priceHistory[]", [
        f("Price", "price", "currency"),
        f("Price Type", "priceType", "string"),
        f("Effective Date", "effectiveDate", "date"),
        f("End Date", "endDate", "date"),
        f("Is Current", "isCurrent", "string"),
      ]),
    ],
    derived: {},
  },

  // -------- IMPORT_BILL : GET /api/import/bill/find/{id} (raw entity) --------
  IMPORT_BILL: {
    label: "Import Bill",
    fields: [
      f("Bill No", "billNo", "string"),
      f("Bill Id", "bill_id", "number"),
      f("Shop", "shop", "string"),
      f("Status", "status", "string"),
      f("Payment Status", "paymentStatus", "string"),
      f("Total", "total", "currency"),
      f("Discount", "discount", "currency"),
      f("Added By", "addedBy", "string"),
      f("Created Date", "createdDate", "date"),
      f("Note", "note", "string"),
      coll("Bill Items", "import_billItems[]", [
        f("Product Code", "imports.product_code", "string"),
        f("Product Name", "imports.itemName", "string"),
        f("Bill Qty", "bill_qty", "number"),
        f("Bill Unit", "billUnit", "string"),
        f("Price / Item", "pricePerItem", "currency"),
        f("Discount / Item", "discount_perItem", "currency"),
        f("Line Price", "price", "currency"),
      ]),
      coll("Payments", "payments[]", [
        f("Type", "type", "string"),
        f("Amount", "amount", "currency"),
        f("Payment Date", "paymentDate", "date"),
      ]),
    ],
    derived: {
      netTotal: { label: "Net Total", op: "subtract", left: "total", right: "discount" },
    },
  },

  // -------- EXPORT_PRODUCT : GET /api/export/product/{id} (flattened DTO) --------
  EXPORT_PRODUCT: {
    label: "Export Product",
    fields: [
      f("Product Id", "product_id", "number"),
      f("Barcode", "barcode", "string"),
      f("Name", "name", "string"),
      f("Existing Qty", "existingQty", "number"),
      f("Status", "status", "string"),
      f("Current Price", "currentPrice", "currency"),
      f("Warehouse", "warehouseName", "string"),
      f("Added By", "addedBy", "string"),
      f("Note", "note", "string"),
      coll("Categories", "categories[]", [
        f("Category", "category", "string"),
        f("Sub Cat 1", "subCat_1", "string"),
      ]),
    ],
    derived: {},
  },

  // -------- EXPORT_PRODUCTION : GET /api/export/production/{id} (DTO) --------
  EXPORT_PRODUCTION: {
    label: "Export Production",
    fields: [
      f("Production Id", "productionId", "number"),
      f("Bill Number", "billNumber", "string"),
      f("Product Name", "productName", "string"),
      f("Product Barcode", "productBarcode", "string"),
      f("Production Qty", "productionQty", "number"),
      f("Sold Qty", "soldQuantity", "number"),
      f("Total Cost", "totalCost", "currency"),
      f("Total Item Cost", "totalItemCost", "currency"),
      f("Unit Price", "unitPrice", "currency"),
      f("Labour Cost", "labourCost", "currency"),
      f("Transport Cost", "transportCost", "currency"),
      f("Packing Cost", "packingCost", "currency"),
      f("Production Date", "productionDate", "date"),
      f("Packing Person", "packingPersonName", "string"),
      f("Warehouse", "warehouseName", "string"),
      f("Note", "note", "string"),
      coll("Materials Used", "items[]", [
        f("Material Name", "materialName", "string"),
        f("Material Code", "materialCode", "string"),
        f("Material Brand", "materialBrand", "string"),
        f("Unit Type", "materialUnitType", "string"),
        f("Unit Price", "unitPrice", "currency"),
        f("Using Qty", "usingQty", "number"),
      ]),
    ],
    derived: {
      remainingQty: { label: "Remaining Qty", op: "subtract", left: "productionQty", right: "soldQuantity" },
    },
  },
};

// --------------------------- helpers ---------------------------

export const getCatalog = (source) => CATALOG[source] || null;
export const getDerived = (source) => (CATALOG[source] && CATALOG[source].derived) || {};

export const sourcesForDomain = (domain) => SOURCES.filter((s) => s.domain === domain);

export const domainForSource = (source) => {
  const s = SOURCES.find((x) => x.key === source);
  return s ? s.domain : null;
};

export const sourceLabel = (source) => {
  const s = SOURCES.find((x) => x.key === source);
  return s ? s.label : source;
};

export const scalarFields = (source) => {
  const c = getCatalog(source);
  return c ? c.fields.filter((x) => !x.isCollection) : [];
};

export const collectionFields = (source) => {
  const c = getCatalog(source);
  return c ? c.fields.filter((x) => x.isCollection) : [];
};

// Sentinel collection path meaning "the list of records themselves" (one row
// per record) rather than a collection nested inside a single record.
export const RECORD_LIST_PATH = "$records[]";

/**
 * Synthetic collection whose rows are the records themselves. Dropping it on a
 * Data table turns that table into a list report; its children are the source's
 * top-level fields, usable as columns.
 */
export function recordListField(source) {
  return {
    label: `Each ${sourceLabel(source)} (one row)`,
    path: RECORD_LIST_PATH,
    type: "collection",
    isCollection: true,
    isRecordList: true,
    children: scalarFields(source),
  };
}

/** Look up a field's declared type by absolute (scalar) or element-relative (table) path. */
export function resolveFieldType(source, path) {
  const c = getCatalog(source);
  if (!c) return "string";
  const top = c.fields.find((x) => x.path === path);
  if (top) return top.type;
  for (const col of c.fields.filter((x) => x.isCollection)) {
    const child = (col.children || []).find((x) => x.path === path);
    if (child) return child.type;
  }
  return "string";
}
